import React, { useState, useRef, useEffect, useCallback } from 'react';
import classnames from 'classnames';

import './WpCollapsible.scss';

export interface Props {
  /** Toggle whether the collapsible is expanded or not. */
  open: boolean;
  /** The content to display inside the collapsible. */
  children?: React.ReactNode;
  /** Assign a transition duration to the collapsible animation in ms. */
  animationDuration?: number;
  /** boolean value to disabl the animation when opening and closing */
  withAnimation?: boolean;
}

type AnimationState = 'idle' | 'measuring' | 'animating';

const WpCollapsible = ({ open, children, animationDuration = 500, withAnimation = true }: Props) => {
  const [height, setHeight] = useState(0);
  const [isOpen, setIsOpen] = useState(open);
  const [animationState, setAnimationState] = useState<AnimationState>('idle');
  const collapsibleContainer = useRef<HTMLDivElement>(null);

  const isFullyOpen = animationState === 'idle' && open && isOpen;
  const isFullyClosed = animationState === 'idle' && !open && !isOpen;
  const content = !isFullyClosed ? children : null;

  const wrapperClassName = classnames('WpCollapsible', isFullyClosed && 'WpCollapsible--isFullyClosed');

  const collapsibleStyles = {
    ...(withAnimation && {
      transitionDuration: `${animationDuration}ms`,
      transitionTimingFunction: 'ease-in-out',
    }),
    ...{
      maxHeight: isFullyOpen ? 'none' : `${height}px`,
      overflow: isFullyOpen ? 'visible' : 'hidden',
    },
  };

  const handleCompleteAnimation = useCallback(
    ({ target }: React.TransitionEvent<HTMLDivElement>) => {
      if (target === collapsibleContainer.current) {
        setAnimationState('idle');
        setIsOpen(open);
      }
    },
    [open]
  );

  useEffect(() => {
    if (open !== isOpen) {
      setAnimationState('measuring');
    }
  }, [open, isOpen]);

  useEffect(() => {
    if (!open || !collapsibleContainer.current) return;
    // If collapsible defaults to open, set an initial height
    setHeight(collapsibleContainer.current.scrollHeight);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!collapsibleContainer.current) return;
    switch (animationState) {
      case 'idle':
        break;
      case 'measuring':
        setHeight(collapsibleContainer.current.scrollHeight);
        setAnimationState('animating');
        break;
      case 'animating':
        setHeight(open ? collapsibleContainer.current.scrollHeight : 0);
    }
  }, [animationState, open, isOpen]);

  return (
    <div
      style={collapsibleStyles}
      ref={collapsibleContainer}
      className={wrapperClassName}
      onTransitionEnd={handleCompleteAnimation}
      aria-expanded={open}
    >
      {content}
    </div>
  );
};

export default WpCollapsible;
